import React, { useState } from "react";
import { useContent } from "../../context/ContentContext";
import { Textarea } from "../../components/ui/textarea";
import { STATUS_DEFAULTS } from "../../components/SiteStatusPage";
import { adminApi } from "../../api";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { KeyRound, Power, Rocket, Wrench, Globe2, ExternalLink } from "lucide-react";

const MODES = [
  { key: "live", label: "Live", desc: "The full website is visible to everyone.", icon: Globe2 },
  { key: "coming_soon", label: "Coming Soon", desc: "Visitors see a launch page, optionally with a countdown.", icon: Rocket },
  { key: "maintenance", label: "Under Maintenance", desc: "Visitors see a \"back shortly\" page while you make changes.", icon: Wrench },
];

function SiteStatusCard() {
  const { toast } = useToast();
  const { content, refresh } = useContent();
  const initial = { mode: "live", title: "", message: "", launch_date: "", ...(content.site_status || {}) };
  const [s, setS] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const defaults = STATUS_DEFAULTS[s.mode];

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/content", { site_status: s });
      await refresh();
      toast({
        title: s.mode === "live" ? "Website is live" : "Website status updated",
        description: s.mode === "live" ? "Visitors can see the full site." : "Visitors now see the placeholder page. You can still see the full site while signed in.",
      });
    } catch {
      toast({ title: "Save failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-2xl border border-border p-6 lg:p-8 max-w-2xl">
      <div className="flex items-center gap-3 mb-5">
        <span className="h-11 w-11 rounded-xl bg-[#eaf4f7] flex items-center justify-center"><Power className="h-5 w-5 text-[#0e7490]" /></span>
        <div>
          <h2 className="font-display text-xl text-[#0f1e33]">Website status</h2>
          <p className="text-sm text-[#5a6b7e]">Switch the public site between live, coming soon and under maintenance.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Website status">
        {MODES.map((m) => {
          const active = s.mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => set("mode", m.key)}
              className={`text-left rounded-xl border p-4 transition-colors ${active ? "border-[#0e7490] bg-[#eaf4f7] ring-1 ring-[#0e7490]" : "border-border hover:bg-secondary"}`}
            >
              <m.icon className={`h-5 w-5 ${active ? "text-[#0e7490]" : "text-[#5a6b7e]"}`} />
              <p className="mt-3 font-medium text-[#0f1e33]">{m.label}</p>
              <p className="mt-1 text-xs text-[#5a6b7e] leading-snug">{m.desc}</p>
            </button>
          );
        })}
      </div>

      {s.mode !== "live" && (
        <div className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Headline <span className="text-[#5a6b7e] font-normal">(optional)</span></Label>
            <Input value={s.title} onChange={(e) => set("title", e.target.value)} placeholder={defaults.title} />
          </div>
          <div className="space-y-1.5">
            <Label>Message <span className="text-[#5a6b7e] font-normal">(optional)</span></Label>
            <Textarea rows={3} value={s.message} onChange={(e) => set("message", e.target.value)} placeholder={defaults.message} />
          </div>
          {s.mode === "coming_soon" && (
            <div className="space-y-1.5 max-w-xs">
              <Label>Launch date &amp; time <span className="text-[#5a6b7e] font-normal">(optional — shows a countdown)</span></Label>
              <Input type="datetime-local" value={s.launch_date} onChange={(e) => set("launch_date", e.target.value)} />
            </div>
          )}
          <p className="text-xs text-[#5a6b7e]">
            Leave the text fields empty to use the default wording. While you are signed in as admin you will still see the full website, with a small notice at the bottom.
          </p>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={saving} className="bg-[#0e7490] hover:bg-[#0b5563] text-white h-11">
          {saving ? "Saving..." : "Save status"}
        </Button>
        <a href="/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#0e7490] hover:underline">
          Open website <ExternalLink className="h-3.5 w-3.5" />
        </a>
        {s.mode !== "live" && (
          <a href="/?preview=status" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#0e7490] hover:underline">
            Preview what visitors see (save first) <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { toast } = useToast();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }
    if (form.new_password.length < 6) {
      toast({ title: "Password too short", description: "Use at least 6 characters.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast({ title: "Password updated" });
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl text-[#0f1e33]">Settings</h1>
      <p className="text-[#5a6b7e] mt-1">Website status and your admin account.</p>

      <SiteStatusCard />

      <div className="mt-6 bg-white rounded-2xl border border-border p-6 lg:p-8 max-w-md">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-11 w-11 rounded-xl bg-[#eaf4f7] flex items-center justify-center"><KeyRound className="h-5 w-5 text-[#0e7490]" /></span>
          <div>
            <h2 className="font-display text-xl text-[#0f1e33]">Change password</h2>
            <p className="text-sm text-[#5a6b7e]">Update your login credentials.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <PasswordInput required value={form.current_password} onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <PasswordInput required value={form.new_password} onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <PasswordInput required value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} />
          </div>
          <Button type="submit" disabled={saving} className="bg-[#0e7490] hover:bg-[#0b5563] text-white h-11">
            {saving ? "Updating..." : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
